// Legacy Top Picks commerce behavior, isolated for campaign cards.
if (!customElements.get('page-campaign-card')) {
  class KiwiTopPicks extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      window.KiwiTopPicksCouponCache = window.KiwiTopPicksCouponCache || new Map();
      this.fallbackCodeCache = window.KiwiTopPicksCouponCache;
      // Capture these controls before theme-level link handlers can navigate away.
      this.addEventListener('click', this.handleClick.bind(this), true);
      this.hydrateFallbackCodes();
    }

    async handleClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      const copyButton = target?.closest('[data-page-campaign-card-copy]');
      if (copyButton && this.contains(copyButton)) {
        event.preventDefault();
        event.stopPropagation();
        await this.copyCode(copyButton);
        return;
      }

      const cartButton = target?.closest('[data-page-campaign-card-cart]');
      if (cartButton && this.contains(cartButton)) {
        event.preventDefault();
        event.stopPropagation();
        await this.addToCart(cartButton);
        return;
      }

      const buyNowButton = target?.closest('[data-page-campaign-card-buy-now]');
      if (
        buyNowButton &&
        this.contains(buyNowButton) &&
        buyNowButton.dataset.pageCampaignCardDirectCheckout === 'true'
      ) {
        const variantId = buyNowButton.getAttribute('data-variant-id');
        if (!variantId || buyNowButton.getAttribute('aria-disabled') === 'true') return;
        event.preventDefault();
        event.stopPropagation();
        await this.buyNowCheckout(buyNowButton);
      }
    }

    readCouponCode(root) {
      const couponCode = root.querySelector('[data-kiwi-coupon-code]');
      const text = (couponCode?.textContent || '').trim();
      if (text && text !== '-') return text;

      const defaultText = (couponCode?.dataset.kiwiCouponDefault || '').trim();
      return defaultText && defaultText !== '-' ? defaultText : '';
    }

    readDefaultCouponCode(root) {
      const couponCode = root.querySelector('[data-kiwi-coupon-code]');
      const defaultText = (couponCode?.dataset.kiwiCouponDefault || '').trim();
      return defaultText && defaultText !== '-' ? defaultText : '';
    }

    getStorageKey(productUrl) {
      if (!productUrl) return '';

      try {
        const url = new URL(productUrl, window.location.origin);
        return `kiwiTopPicksCoupon:${url.pathname}`;
      } catch (_) {
        return '';
      }
    }

    readStoredCouponCode(productUrl) {
      const key = this.getStorageKey(productUrl);
      if (!key) return null;

      try {
        return window.localStorage.getItem(key);
      } catch (_) {
        return null;
      }
    }

    storeCouponCode(productUrl, code) {
      const key = this.getStorageKey(productUrl);
      if (!key) return;

      try {
        window.localStorage.setItem(key, code || '');
      } catch (_) {
        // localStorage can be unavailable in private browsing.
      }
    }

    async getFallbackCouponCode(productUrl) {
      const localCode = this.readCouponCode(document);
      if (!productUrl) return localCode;

      const url = new URL(productUrl, window.location.origin).toString();
      if (url === window.location.href) return localCode;

      if (!this.fallbackCodeCache.has(url)) {
        this.fallbackCodeCache.set(
          url,
          fetch(url, { credentials: 'same-origin' })
            .then((response) => (response.ok ? response.text() : ''))
            .then((html) => {
              if (!html) return '';
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const code = this.readCouponCode(doc);
              this.storeCouponCode(url, code);
              return code;
            })
            .catch(() => null)
        );
      }

      return this.fallbackCodeCache.get(url);
    }

    setCode(button, code) {
      if (!code) {
        button.hidden = true;
        button.setAttribute('data-clipboard-text', '');
        const value = button.querySelector('.page-campaign-card__code-value');
        if (value) value.textContent = '';
        return;
      }

      const value = button.querySelector('.page-campaign-card__code-value');
      if (value) value.textContent = code;
      button.setAttribute('data-clipboard-text', code);
      button.hidden = false;
    }

    getProductUrl(button) {
      if (button.dataset.pageCampaignCardProductUrl) return button.dataset.pageCampaignCardProductUrl;

      const card = button.closest('.page-campaign-card__card');
      const buyLink = card?.querySelector('.page-campaign-card__buy');
      return buyLink?.getAttribute('href') || '';
    }

    hydrateFallbackCodes() {
      const pageDefaultCode = this.readDefaultCouponCode(document);
      this.querySelectorAll('[data-page-campaign-card-copy]').forEach(async (button) => {
        const currentCode = (button.getAttribute('data-clipboard-text') || '').trim();
        if (currentCode) {
          this.setCode(button, currentCode);
          return;
        }

        const productUrl = this.getProductUrl(button);
        const storedCode = this.readStoredCouponCode(productUrl);
        if (storedCode !== null) {
          this.setCode(button, storedCode);
        } else if (pageDefaultCode) {
          this.setCode(button, pageDefaultCode);
        }

        const fallbackCode = await this.getFallbackCouponCode(productUrl);
        if (fallbackCode !== null) this.setCode(button, fallbackCode);
      });
    }

    async copyCode(button) {
      const text = button.getAttribute('data-clipboard-text') || '';
      if (!text) return;

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const helper = document.createElement('textarea');
          helper.value = text;
          helper.setAttribute('readonly', '');
          helper.style.position = 'absolute';
          helper.style.left = '-9999px';
          document.body.appendChild(helper);
          helper.select();
          document.execCommand('copy');
          helper.remove();
        }

        const copiedText = button.getAttribute('data-copied-text') || 'Copied';
        const label = button.querySelector('[data-page-campaign-card-copy-label]');
        if (label) {
          label.textContent = copiedText;
        } else {
          button.textContent = copiedText;
        }
        button.classList.add('is-copied');
      } catch (_) {
        // Keep the current label when clipboard access is blocked.
      }
    }

    async ensureCartDrawerReady() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return null;

      const loadTemplateContent = (element) => {
        element?.querySelectorAll('template.deferred').forEach((template) => {
          const fragment = template.content.cloneNode(true);
          fragment.querySelectorAll('script, lazy-script').forEach((lazyScript) => {
            const script = document.createElement('script');
            Array.from(lazyScript.attributes).forEach((attribute) => {
              script.setAttribute(attribute.name, attribute.value);
            });
            script.async = false;
            script.textContent = lazyScript.textContent;
            lazyScript.replaceWith(script);
          });
          template.parentNode.insertBefore(fragment, template.nextSibling);
          template.remove();
        });
      };

      if (typeof window.loadTemplateContent === 'function') {
        window.loadTemplateContent(drawer);
      } else {
        loadTemplateContent(drawer);
      }

      if (window.customElements?.whenDefined && !window.customElements.get('cart-drawer')) {
        const source = Array.from(document.scripts).find((script) => script.src.includes('/cart-drawer.js'))?.src;
        if (source && !document.querySelector('script[data-kiwi-cart-drawer-loader]')) {
          await new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = source;
            script.async = false;
            script.dataset.kiwiCartDrawerLoader = 'true';
            script.onload = resolve;
            script.onerror = resolve;
            document.head.appendChild(script);
          });
        }

        await Promise.race([
          window.customElements.whenDefined('cart-drawer'),
          new Promise((resolve) => window.setTimeout(resolve, 700)),
        ]);
      }

      return drawer;
    }

    getCartTargets() {
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = cartDrawer ? null : document.querySelector('cart-notification');
      const cartItems = cartDrawer ? null : document.querySelector('cart-items');
      const sectionIds = new Set();

      if (cartDrawer?.getSectionsToRender) {
        cartDrawer.getSectionsToRender().forEach((section) => sectionIds.add(section.id));
      } else if (cartNotification?.getSectionsToRender) {
        window.loadTemplateContent?.(cartNotification);
        cartNotification.getSectionsToRender().forEach((section) => sectionIds.add(section.id));
      }

      return {
        cartDrawer,
        cartNotification,
        cartItems,
        sectionIds: Array.from(sectionIds),
      };
    }

    publishCartUpdate(response, variantId) {
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-form',
        productVariantId: String(variantId || response?.id || '').trim(),
        cartData: response,
      });
    }

    openCartDrawer(cartDrawer, trigger) {
      if (!cartDrawer || typeof cartDrawer.open !== 'function') return;
      window.requestAnimationFrame(() => cartDrawer.open(trigger));
    }

    async renderCart(response, trigger, drawerReadyPromise) {
      if (drawerReadyPromise) {
        await drawerReadyPromise;
      } else {
        await this.ensureCartDrawerReady();
      }

      const { cartDrawer, cartNotification, cartItems } = this.getCartTargets();

      if (cartDrawer) {
        cartDrawer.init?.();
        cartDrawer.setActiveElement?.(trigger);
        this.openCartDrawer(cartDrawer, trigger);

        const sectionsToRender = cartDrawer.getSectionsToRender?.() || [];
        const hasAllSections = sectionsToRender.length > 0 && sectionsToRender.every((section) => response.sections?.[section.id]);
        if (!hasAllSections && sectionsToRender.length) {
          const sectionIds = cartDrawer.getSectionsToRender().map((section) => section.id);
          if (sectionIds.length) {
            const sectionsUrl = new URL(window.location.pathname || '/', window.location.origin);
            sectionsUrl.searchParams.set('sections', sectionIds.join(','));
            const sectionResponse = await fetch(sectionsUrl.toString(), {
              credentials: 'same-origin',
              headers: { Accept: 'application/json' },
            });
            if (!sectionResponse.ok) throw new Error(`Cart sections request failed with HTTP ${sectionResponse.status}.`);
            response = {
              ...response,
              sections: await sectionResponse.json(),
            };
          }
        }

        if (typeof cartDrawer.renderContents === 'function' && response.sections) {
          cartDrawer.renderContents(response);
        } else {
          window.location.href = window.routes?.cart_url || '/cart';
        }
        return;
      }

      if (cartNotification?.renderContents) {
        cartNotification.renderContents(response);
        return;
      }

      if (cartItems?.renderContents) {
        cartItems.renderContents(response);
        return;
      }

      window.location.href = window.routes?.cart_url || '/cart';
    }

    async addToCart(button) {
      const variantId = button.getAttribute('data-variant-id');
      if (!/^\d+$/.test(variantId || '') || button.disabled || button.dataset.loading === 'true') return;

      const drawerReadyPromise = this.ensureCartDrawerReady().catch(() => null);
      const { sectionIds } = this.getCartTargets();
      const payload = {
        items: [
          {
            id: Number(variantId),
            quantity: 1,
          },
        ],
      };

      if (sectionIds.length > 0) {
        payload.sections = [...new Set(sectionIds)];
        payload.sections_url = window.location.pathname;
      }

      button.dataset.loading = 'true';
      button.classList.add('is-loading');
      button.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Unable to add this item to the cart.');
        }

        this.publishCartUpdate(data, variantId);
        await this.renderCart(data, button, drawerReadyPromise);
      } catch (error) {
        console.error(error);
      } finally {
        button.dataset.loading = 'false';
        button.classList.remove('is-loading');
        button.disabled = button.getAttribute('aria-disabled') === 'true';
      }
    }

    async buyNowCheckout(button) {
      const variantId = button.getAttribute('data-variant-id')?.trim();
      if (!/^\d+$/.test(variantId || '') || button.dataset.loading === 'true') return;

      button.dataset.loading = 'true';
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      try {
        const cartPath = (window.routes?.cart_url || '/cart').replace(/\/+$/, '');
        const checkoutUrl = new URL(`${cartPath}/${variantId}:1`, window.location.origin);
        checkoutUrl.search = 'checkout';
        window.location.assign(checkoutUrl.toString());
      } catch (error) {
        console.error(error);
        button.dataset.loading = 'false';
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('page-campaign-card', KiwiTopPicks);
}
